import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import {
  renderArticles,
  renderGeoChildren,
  renderDynamicComponents,
  renderHighlightsSection,
  renderHotels,
  renderGeoLongText,
  renderProductGallery,
  renderProducts,
  renderSiblings,
  renderThemeGeoPages,
  renderBestTravelPeriod,
  renderGeoStaticComponents,
} from 'components/domain/fdr-geo/fdr-geo-render.utils';
import { isFdrHotelProduct } from 'components/domain/fdr-product/fdr-product.utils';
import { FdrPageComponentsFragment } from 'schemas/fragment/page-components/fdr-page-components.generated';
import { FdrGeoContentQuery } from 'schemas/query/geo/fdr-geo-content.generated';
import { GeoComponents } from './fdr-geo.constants';
import { TFdrThGeoModuleObject } from './fdr-geo.types';
import { getFdrGeoChildren } from './fdr-geo.utils';
import useMap from './useMap';

export const useGeoComponentsMap = (
  id: string,
  isCountry: boolean,
  geo: FdrGeoContentQuery['fdrGeo'],
  isB2B: boolean,
  geoModules: TFdrThGeoModuleObject,
  isNewMapDesignEnabled: boolean,
  pageComponents: FdrPageComponentsFragment['components'],
  geoStaticPageComponents: FdrPageComponentsFragment['components']
): Map<GeoComponents, () => React.ReactElement> => {
  const [t] = useTranslation('geo');

  const { bestTravelPeriod } = geo || {};

  const hotels = geoModules?.hotels?.productsPage?.filter(isFdrHotelProduct);
  const geoChildren = getFdrGeoChildren(geoModules);
  const geoObjectName = geo.name;

  const mapRender = useMap(geo, isCountry, isB2B, isNewMapDesignEnabled, id);

  const themeRender = useCallback(() => {
    return renderThemeGeoPages(id, geoModules?.themeGeoPages, geoObjectName);
  }, [id, geoModules?.themeGeoPages, geoObjectName]);

  const mediaRender = useCallback(() => {
    return renderProductGallery(
      id,
      geoModules?.products,
      geoModules?.products?.pagination.total,
      geoObjectName
    );
  }, [geoModules?.products, geoObjectName]);

  const geoChildrenRender = useCallback(() => {
    return renderGeoChildren(geoChildren, geoObjectName);
  }, [geoChildren]);

  const hotelsRender = useCallback(() => {
    return renderHotels(
      hotels,
      t('geo:extra.hotels.title', { geoObject: geoObjectName }),
      isB2B,
      t('geo:regular.hotels.title', { geoObject: geoObjectName }),
      t('geo:extra.hotels.header.title'),
      t('geo:navigation.hotelsB2b')
    );
  }, [isB2B, hotels, geoObjectName]);

  const productsRender = useCallback(() => {
    return renderProducts(id, geoModules, geoObjectName);
  }, [id, geoModules, geoObjectName]);

  const articlesRender = useCallback(() => {
    return renderArticles(
      id,
      geoModules.relatedBlogArticles,
      t('geo:blogArticles.title', { geoObject: geoObjectName })
    );
  }, [geoModules.relatedBlogArticles, geoObjectName]);

  const pageComponentsRender = useCallback(() => {
    return renderDynamicComponents(pageComponents);
  }, [pageComponents]);

  const geoStaticComponentsRender = useCallback(() => {
    return renderGeoStaticComponents(geoStaticPageComponents);
  }, [geoStaticPageComponents]);

  const longTextRender = useCallback(() => {
    return renderGeoLongText(geo?.longTextBlocks, geo?.longTitle);
  }, [geo?.longTextBlocks, geo?.longTitle]);

  const siblingsRender = useCallback(() => {
    return renderSiblings(id);
  }, [id]);

  const highlightsSectionRender = useCallback(() => {
    return renderHighlightsSection(geo?.highlightsTitle, geo?.highlightsText);
  }, [geo?.highlightsText, geo?.highlightsTitle]);

  const bestTravelPeriodRender = useCallback(() => {
    return renderBestTravelPeriod(
      bestTravelPeriod?.title,
      bestTravelPeriod?.text,
      bestTravelPeriod?.informationAccordion
    );
  }, [bestTravelPeriod]);

  return new Map<GeoComponents, () => React.ReactElement>([
    [GeoComponents.MAP, isB2B ? null : mapRender],
    [GeoComponents.THEMES, themeRender],
    [GeoComponents.MEDIA, mediaRender],
    [GeoComponents.DESTINATIONS, geoChildrenRender],
    [GeoComponents.HOTELS, hotelsRender],
    [GeoComponents.ARTICLES, articlesRender],
    [GeoComponents.LONG_TEXT, longTextRender],
    [GeoComponents.DYNAMIC_COMPONENTS, pageComponentsRender],
    [GeoComponents.GEO_STATIC_COMPONENTS, geoStaticComponentsRender],
    [GeoComponents.SIBLINGS, siblingsRender],
    [GeoComponents.BEST_TRAVEL_PERIOD, bestTravelPeriodRender],
    [GeoComponents.HIGHLIGHTS_SECTION, highlightsSectionRender],
    [GeoComponents.PRODUCTS, productsRender],
  ]);
};
